/**
 * CII (Country Instability Index) choropleth layer renderer.
 * GeoJsonLayer painting the full country set (WM DeckGLMap.ts:4674-4694):
 * scored countries fill by CII level, unscored countries stay transparent.
 * Scores come from the analysis engine; classification thresholds live in
 * intelligence/analysis/cii.ts so the map and the panels agree.
 */

import { GeoJsonLayer } from '@deck.gl/layers';
import type { Layer } from '@deck.gl/core';
import type { Feature } from 'geojson';
import { getAllCountryFeatures } from '@/map/country-detect';
import { getCIIClassification } from '@/intelligence/analysis/cii';

/** Layer IDs managed by this renderer. */
export const CII_CHOROPLETH_LAYER_IDS = new Set(['cii-choropleth']);

type CIILevel = ReturnType<typeof getCIIClassification>;

/** Fill colors by CII level — WM src/config/cii-colors.ts:3-9. */
export const CII_LEVEL_COLORS: Record<CIILevel, [number, number, number, number]> = {
  low: [40, 180, 60, 130],
  normal: [220, 200, 50, 135],
  elevated: [240, 140, 30, 145],
  high: [220, 50, 20, 155],
  critical: [140, 10, 0, 170],
};

const TRANSPARENT: [number, number, number, number] = [0, 0, 0, 0];

/** The subset of CIIScore the choropleth needs. */
export interface CIIScoreLike {
  overall: number;
}

/** Create the deck.gl CII choropleth layer from per-country scores. */
export function createCIIChoroplethLayer(
  scores: Map<string, CIIScoreLike>,
): Layer[] {
  const features = getAllCountryFeatures();
  if (features.length === 0 || scores.size === 0) return [];

  return [
    new GeoJsonLayer<Feature>({
      id: 'cii-choropleth',
      data: { type: 'FeatureCollection' as const, features: features as unknown as Feature[] },
      pickable: true,
      filled: true,
      stroked: true,
      lineWidthUnits: 'pixels' as const,
      getFillColor: (f: Feature) => {
        const code = f.properties?.code as string | undefined;
        const score = code ? scores.get(code.toUpperCase()) : undefined;
        return score ? CII_LEVEL_COLORS[getCIIClassification(score.overall)] : TRANSPARENT;
      },
      getLineColor: [80, 80, 80, 80],
      getLineWidth: 1,
      lineWidthMinPixels: 0.5,
      updateTriggers: {
        getFillColor: [scores.size],
      },
    }),
  ];
}
